import { apiRequest } from '../../../services/api/client';

export interface EbdReportTotals {
  bibles_count: number;
  present_count: number;
  absent_count: number;
  visitors_count: number;
  classes_count: number;
  lessons_count: number;
}

export interface EbdReportClassGroup {
  class_id: number;
  class_name: string;
  totals: EbdReportTotals;
}

export interface EbdReportSegmentGroup {
  segment_id: number;
  segment_name: string;
  totals: EbdReportTotals;
  classes: EbdReportClassGroup[];
}

export interface EbdReportChurchGroup {
  church_id: number;
  church_name: string;
  totals: EbdReportTotals;
  segments: EbdReportSegmentGroup[];
}

export interface EbdWeeklyReport {
  quarter: { id: number; name: string; year: number; quarter_number: number; starts_on: string; ends_on: string };
  period: { starts_on: string | null; ends_on: string | null };
  totals: EbdReportTotals;
  weeks: Array<{ week_key: string; starts_on: string; ends_on: string; totals: EbdReportTotals }>;
  groups: EbdReportChurchGroup[];
}

export interface EbdMonthlyReport {
  quarter: { id: number; name: string; year: number; quarter_number: number; starts_on: string; ends_on: string };
  period: { starts_on: string | null; ends_on: string | null };
  totals: EbdReportTotals;
  months: Array<{ month: string; totals: EbdReportTotals }>;
  groups: EbdReportChurchGroup[];
}

export interface EbdClassDiaryEntry {
  taught_on: string;
  church: { id: number; name: string };
  segment: { id: number; name: string };
  class: { id: number; name: string };
  lesson: { id: number; number: number; title: string };
  teacher_name: string;
  bibles_count: number;
  present_count: number;
  absent_count: number;
  visitors_count: number;
}

export interface EbdClassDiaryReport {
  quarter: { id: number; name: string; year: number; quarter_number: number; starts_on: string; ends_on: string };
  period: { starts_on: string | null; ends_on: string | null };
  totals: EbdReportTotals;
  entries: EbdClassDiaryEntry[];
}

export interface EbdAttendanceStudentRow {
  student_id: number;
  student_name: string;
  church_id: number;
  church_name: string;
  class_name: string;
  present_count: number;
  absent_count: number;
  total_lessons: number;
  attendance_rate: number;
}

export interface EbdStudentsAttendanceChurch {
  church_id: number;
  church_name: string;
  top_present: EbdAttendanceStudentRow[];
  absentees: EbdAttendanceStudentRow[];
  honor_students: EbdAttendanceStudentRow[];
}

export interface EbdStudentsAttendanceReport {
  quarter: { id: number; name: string; year: number; quarter_number: number; starts_on: string; ends_on: string };
  period: { starts_on: string | null; ends_on: string | null };
  churches: EbdStudentsAttendanceChurch[];
}

const buildQuery = (quarterId: string, dateFrom?: string, dateTo?: string) => {
  const params = new URLSearchParams();
  params.set('quarter_id', quarterId);

  if (dateFrom) {
    params.set('date_from', dateFrom);
  }

  if (dateTo) {
    params.set('date_to', dateTo);
  }

  return `?${params.toString()}`;
};

export const ebdReportsService = {
  weekly: async (quarterId: string, dateFrom?: string, dateTo?: string) => {
    const response = await apiRequest<{ data: EbdWeeklyReport }>(`/ebd/reports/ebd/weekly${buildQuery(quarterId, dateFrom, dateTo)}`);
    return response.data;
  },
  monthly: async (quarterId: string, dateFrom?: string, dateTo?: string) => {
    const response = await apiRequest<{ data: EbdMonthlyReport }>(`/ebd/reports/ebd/monthly${buildQuery(quarterId, dateFrom, dateTo)}`);
    return response.data;
  },
  classDiary: async (quarterId: string, dateFrom?: string, dateTo?: string) => {
    const response = await apiRequest<{ data: EbdClassDiaryReport }>(`/ebd/reports/ebd/class-diary${buildQuery(quarterId, dateFrom, dateTo)}`);
    return response.data;
  },
  studentsAttendance: async (quarterId: string, dateFrom?: string, dateTo?: string) => {
    const response = await apiRequest<{ data: EbdStudentsAttendanceReport }>(`/ebd/reports/ebd/students-attendance${buildQuery(quarterId, dateFrom, dateTo)}`);
    return response.data;
  },
  downloadPdf: async (reportType: string, quarterId: string, dateFrom?: string, dateTo?: string): Promise<Blob> => {
    const params = new URLSearchParams();
    params.set('report_type', reportType);
    params.set('quarter_id', quarterId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    const configuredApiUrl = process.env.REACT_APP_API_URL ?? process.env.VITE_API_URL;
    const baseUrl = (configuredApiUrl
      ? configuredApiUrl
      : `${window.location.protocol === 'https:' ? 'https:' : 'http:'}//${window.location.hostname}:8000/api/v1`
    ).replace(/\/$/, '');
    const churchId = window.localStorage.getItem('current_church_id');

    const response = await fetch(`${baseUrl}/ebd/reports/ebd/pdf?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/pdf',
        ...(churchId ? { 'X-Church-Id': churchId } : {}),
      },
    });

    if (!response.ok) {
      throw new Error('Não foi possível gerar o PDF no servidor.');
    }

    return response.blob();
  },
};
